import React, { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Upload, Copy, Check, Loader2 } from 'lucide-react';
import { trackEvent } from '../analytics';
import LanguageDropdown from './LanguageDropdown';

const API_BASE = 'https://cloudfare-worker-altvision-api.chris-172.workers.dev';

const LANGUAGE_NAMES: Record<string, string> = {
  en: 'English', es: 'Spanish', fr: 'French', de: 'German', pt: 'Portuguese',
  vi: 'Vietnamese', sv: 'Swedish', ro: 'Romanian', ar: 'Arabic', bn: 'Bengali',
  cs: 'Czech', da: 'Danish', el: 'Greek', fi: 'Finnish', he: 'Hebrew',
  hi: 'Hindi', hu: 'Hungarian', id: 'Indonesian', it: 'Italian', ja: 'Japanese',
  ko: 'Korean', nl: 'Dutch', no: 'Norwegian', pl: 'Polish', ru: 'Russian',
  ta: 'Tamil', th: 'Thai', tr: 'Turkish', uk: 'Ukrainian', ur: 'Urdu', zh: 'Chinese',
};

const ALLOWED_TYPES = ['image/png', 'image/jpeg', 'image/gif', 'image/webp', 'image/bmp'];
const MAX_SIZE = 16 * 1024 * 1024;

function parseAltTextAndTags(message: string): { altText: string; tags: string[] } {
  const match = message.match(/ALT TEXT:\s*([\s\S]*?)\s*TAGS:\s*([\s\S]*)/i);
  if (!match) return { altText: message.trim(), tags: [] };
  const tags = match[2]
    .split(',')
    .map((tag) => tag.trim())
    .filter(Boolean);
  return { altText: match[1].trim(), tags };
}

const MAX_DIMENSION = 512;
const TARGET_SIZE = 200_000;

async function compressImage(dataUrl: string): Promise<string> {
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => {
      let { width, height } = img;
      if (width > MAX_DIMENSION || height > MAX_DIMENSION) {
        const ratio = Math.min(MAX_DIMENSION / width, MAX_DIMENSION / height);
        width  = Math.round(width  * ratio);
        height = Math.round(height * ratio);
      }

      const canvas = document.createElement('canvas');
      canvas.width  = width;
      canvas.height = height;
      const ctx = canvas.getContext('2d')!;
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, width, height);
      ctx.drawImage(img, 0, 0, width, height);

      let quality = 0.6;
      let result  = canvas.toDataURL('image/jpeg', quality);
      while (result.length > TARGET_SIZE * 1.37 && quality > 0.15) {
        quality -= 0.1;
        result   = canvas.toDataURL('image/jpeg', quality);
      }
      resolve(result);
    };
    img.src = dataUrl;
  });
}

const ImageUploadDemo: React.FC = () => {
  const { t, i18n } = useTranslation();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const uploadZoneRef = useRef<HTMLDivElement>(null);

  const [file, setFile] = useState<File | null>(null);
  const [uploadRequired, setUploadRequired] = useState(false);
  const [preview, setPreview] = useState<string | null>(null);
  const [keywords, setKeywords] = useState('');
  const [keywordsEnabled, setKeywordsEnabled] = useState(false);
  const [loading, setLoading] = useState(false);
  const [altText, setAltText] = useState<string | null>(null);
  const [tags, setTags] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [copiedField, setCopiedField] = useState<'alt' | 'tags' | null>(null);

  const validateAndSetFile = (f: File) => {
    setAltText(null);
    setTags([]);
    setError(null);
    setUploadRequired(false);
    if (!ALLOWED_TYPES.includes(f.type)) {
      setError(t('home.demo.errorInvalidType'));
      return;
    }
    if (f.size > MAX_SIZE) {
      setError(t('home.demo.errorFileTooLarge'));
      return;
    }
    setFile(f);
    const reader = new FileReader();
    reader.onload = async (e) => {
      const raw = e.target?.result as string;
      const compressed = await compressImage(raw);
      setPreview(compressed);
    };
    reader.readAsDataURL(f);
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    if (f) { validateAndSetFile(f); trackEvent('file_upload', { method: 'click' }); }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    const f = e.dataTransfer.files?.[0];
    if (f) { validateAndSetFile(f); trackEvent('file_upload', { method: 'drag_drop' }); }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = () => setIsDragging(false);

  const handleGenerate = async () => {
    if (!file || !preview) {
      setUploadRequired(true);
      uploadZoneRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      uploadZoneRef.current?.focus();
      return;
    }
    setLoading(true);
    setAltText(null);
    setTags([]);
    setError(null);
    trackEvent('generate_alt_text');

    try {
      // Step 1: fetch/process image
      const fetchRes = await fetch(`${API_BASE}/proxy/fetch-image`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-Source': 'Web-App' },
        body: JSON.stringify({ url: preview }),
      });
      if (!fetchRes.ok) throw new Error('fetch-image failed');
      const fetchData = await fetchRes.json();
      const base64Image: string = fetchData.base64;

      // Step 2: generate alt text + tags
      const langCode = i18n.language?.split('-')[0] || 'en';
      const langName = LANGUAGE_NAMES[langCode] || 'English';
      let promptText = `Write the alt text in ${langName}, then on a new line write 6-8 short, comma-separated SEO tags relevant to the image (no hashtags, no numbering). Format your response exactly as:\nALT TEXT: <alt text>\nTAGS: <tag1, tag2, tag3>`;
      if (keywordsEnabled && keywords.trim()) {
        promptText += ` Adjacent content: ${keywords.trim()}`;
      }

      const visionRes = await fetch(`${API_BASE}/proxy/process-simple-vision-api`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-Source': 'Web-App' },
        body: JSON.stringify({ base64Image, promptText, threadId: null }),
      });
      if (!visionRes.ok) throw new Error('process-simple-vision-api failed');
      const visionData = await visionRes.json();

      if (visionData.message && (!visionData.code || visionData.code === 'success')) {
        const parsed = parseAltTextAndTags(visionData.message);
        setAltText(parsed.altText);
        setTags(parsed.tags);
        trackEvent('generate_success');
      } else if (visionData.code === 'limit') {
        setError(t('home.demo.errorRateLimit'));
        trackEvent('generate_error', { reason: 'rate_limit' });
      } else if (visionData.message) {
        setError(visionData.message);
      } else {
        throw new Error('Unexpected response');
      }
    } catch {
      setError(t('home.demo.errorGenerate'));
      trackEvent('generate_error', { reason: 'api_error' });
    } finally {
      setLoading(false);
    }
  };

  const handleCopy = (field: 'alt' | 'tags') => {
    const text = field === 'alt' ? altText : tags.join(', ');
    if (!text) return;
    navigator.clipboard.writeText(text).then(() => {
      setCopiedField(field);
      setTimeout(() => setCopiedField(null), 2000);
      trackEvent(field === 'alt' ? 'copy_alt_text' : 'copy_tags');
    });
  };

  return (
    <div className="bg-white border border-ink rounded-lg p-6 space-y-4 shadow-[8px_8px_0_rgba(19,28,43,0.9)]">
      {/* Output language */}
      <div className="flex items-center justify-between border-b border-ink/10 pb-3 -mx-6 px-6">
        <span className="font-mono text-xs uppercase tracking-[0.15em] text-ink-soft">{t('home.demo.outputLanguage')}</span>
        <LanguageDropdown variant="inline" />
      </div>

      {/* Upload zone */}
      <div
        ref={uploadZoneRef}
        tabIndex={-1}
        className={`border-2 border-dashed rounded-md p-6 text-center cursor-pointer transition-colors outline-none ${
          uploadRequired ? 'border-red-600 bg-red-50' : isDragging ? 'border-azure-bright bg-azure-bright/10' : 'border-ink/25 bg-paper/60 hover:border-ink/50'
        }`}
        onClick={() => fileInputRef.current?.click()}
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
      >
        <input
          ref={fileInputRef}
          type="file"
          accept={ALLOWED_TYPES.join(',')}
          className="hidden"
          onChange={handleFileChange}
        />

        {preview ? (
          <img
            src={preview}
            alt="Preview"
            className="mx-auto max-h-48 rounded-lg object-contain enter-rise"
          />
        ) : (
          <>
            <Upload className="mx-auto mb-3 text-ink/40" size={36} strokeWidth={1.5} />
            <div className="flex flex-col sm:flex-row items-center justify-center gap-1 sm:gap-2 mb-1">
              <span className="bg-azure text-white font-mono text-xs font-semibold uppercase tracking-wide px-2 py-0.5 rounded-sm">
                {t('home.demo.tryIt')}
              </span>
              <span className="text-ink/80 text-sm text-center">{t('home.demo.dragDrop')}</span>
            </div>
            <p className="text-ink-soft text-xs mb-3">{t('home.demo.fileTypes')}</p>
          </>
        )}

        <button
          type="button"
          className={`mt-3 text-sm px-4 py-1.5 rounded-md active:scale-[0.97] transition-[background-color,color,border-color,transform] duration-150 ease-out-strong ${
            preview
              ? 'border border-dashed border-ink/30 text-ink-soft hover:text-ink hover:border-ink/60 bg-transparent'
              : 'bg-ink text-paper hover:bg-ink/80'
          }`}
          onClick={(e) => { e.stopPropagation(); fileInputRef.current?.click(); }}
        >
          {preview ? t('home.demo.replaceFile') : t('home.demo.chooseFile')}
        </button>
      </div>

      {/* Error */}
      {error && (
        <p role="alert" className="text-red-600 text-sm enter-drop">{error}</p>
      )}

      {/* Keywords toggle */}
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <span className="text-sm text-ink/80">{t('home.demo.seoKeywords')}</span>
          <div className="flex items-center gap-2">
            <span className="text-xs text-ink-soft">{t('home.demo.optional')}</span>
            <button
              type="button"
              role="switch"
              aria-checked={keywordsEnabled}
              onClick={() => setKeywordsEnabled((v) => !v)}
              className={`relative w-10 h-5 rounded-full transition-colors ${
                keywordsEnabled ? 'bg-azure' : 'bg-ink/20'
              }`}
            >
              <span
                className={`absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform duration-150 ease-out-strong ${
                  keywordsEnabled ? 'translate-x-5' : 'translate-x-0'
                }`}
              />
            </button>
          </div>
        </div>

        {keywordsEnabled && (
          <input
            type="text"
            value={keywords}
            onChange={(e) => setKeywords(e.target.value)}
            placeholder="e.g. product photography outdoor"
            className="w-full bg-paper border border-ink/20 rounded-md px-3 py-2 text-sm text-ink placeholder-ink/40 focus:outline-none focus:border-azure enter-drop"
          />
        )}
      </div>

      {/* Generate button */}
      <button
        type="button"
        disabled={loading}
        onClick={handleGenerate}
        className="w-full bg-azure hover:bg-azure-deep disabled:bg-ink/10 disabled:text-ink/40 text-white font-semibold py-2.5 rounded-md active:scale-[0.98] disabled:active:scale-100 transition-[background-color,color,transform] duration-150 ease-out-strong flex items-center justify-center gap-2"
      >
        {loading ? (
          <>
            <Loader2 size={16} className="animate-spin [animation-duration:0.7s]" />
            {t('home.demo.generating')}
          </>
        ) : (
          t('home.demo.generateButton')
        )}
      </button>

      {/* Results */}
      {altText && (
        <div className="space-y-3 enter-rise">
          <div className="bg-paper border border-ink/15 rounded-md p-4">
            <div className="flex items-center justify-between mb-2">
              <span className="font-mono text-xs font-medium text-ink-soft uppercase tracking-[0.15em]">
                {t('home.demo.resultLabel')}
              </span>
              <button
                type="button"
                onClick={() => handleCopy('alt')}
                className="flex-shrink-0 text-ink-soft hover:text-azure active:scale-90 transition-[color,transform] duration-150 ease-out-strong"
                aria-label={copiedField === 'alt' ? t('home.demo.copied') : t('home.demo.copyButton')}
              >
                {copiedField === 'alt' ? <Check size={16} className="enter-pop" /> : <Copy size={16} />}
              </button>
            </div>
            <p className="text-ink text-sm leading-relaxed">
              <span className="font-mono text-azure" aria-hidden="true">alt="</span>
              {altText}
              <span className="font-mono text-azure" aria-hidden="true">"</span>
            </p>
          </div>

          {tags.length > 0 && (
            <div className="bg-paper border border-ink/15 rounded-md p-4">
              <div className="flex items-center justify-between mb-2">
                <span className="font-mono text-xs font-medium text-ink-soft uppercase tracking-[0.15em]">
                  {t('home.demo.tagsLabel')}
                </span>
                <button
                  type="button"
                  onClick={() => handleCopy('tags')}
                  className="flex-shrink-0 text-ink-soft hover:text-azure active:scale-90 transition-[color,transform] duration-150 ease-out-strong"
                  aria-label={copiedField === 'tags' ? t('home.demo.copied') : t('home.demo.copyButton')}
                >
                  {copiedField === 'tags' ? <Check size={16} className="enter-pop" /> : <Copy size={16} />}
                </button>
              </div>
              <div className="flex flex-wrap gap-1.5">
                {tags.map((tag, i) => (
                  <span
                    key={tag}
                    className="font-mono text-xs text-ink bg-paper-deep border border-ink/15 rounded-sm px-2 py-0.5 enter-rise"
                    style={{ animationDelay: `${i * 40}ms` }}
                  >
                    {tag}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ImageUploadDemo;
